import React from 'react';
import { Button } from '../ui';
// NOTE: Hero is the LCP element and must render fully visible immediately.
// The entrance choreography below is CSS-only (no libraries) and is fully
// disabled under prefers-reduced-motion. Content is present in the DOM at
// all times for SEO and accessibility.
export function Hero() {
  return <section id="top" className="relative min-h-[90vh] flex items-center pt-32 pb-24 md:py-32 bg-atrium-ivory overflow-hidden scroll-mt-24" data-id="element-235">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full" data-id="element-236">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24" data-id="element-237">
          {/* Text Content (Left ~45%) */}
          <div className="w-full lg:w-[45%] z-10" data-id="element-238">
            {/* Eyebrow with a thin bronze rule that draws in */}
            <div className="flex items-center gap-4 mb-6" data-id="element-239">
              <span className="atrium-rise block text-xs font-bold tracking-[0.15em] uppercase text-atrium-green" style={{
              animationDelay: '0.05s'
            }} data-id="element-240">
                Perth - Luxury Custom Home Builder
              </span>
            </div>

            <div className="relative" data-id="element-241">
              {/* Vertical Atrium Line accent beside the headline */}
              <span className="atrium-rule hidden md:block absolute -left-6 top-1 bottom-1 w-[1px] bg-atrium-bronze" aria-hidden="true" data-id="element-242" />

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-atrium-ink leading-[1.1] mb-8 text-balance" data-id="element-243">
                <span className="atrium-headline-mask" data-id="element-244">
                  <span className="atrium-headline-line" style={{
                  animationDelay: '0.18s'
                }} data-id="element-245">
                    Perth luxury custom homes,
                  </span>
                </span>
                <span className="atrium-headline-mask" data-id="element-246">
                  <span className="atrium-headline-line" style={{
                  animationDelay: '0.3s'
                }} data-id="element-247">
                    built with three generations
                  </span>
                </span>
                <span className="atrium-headline-mask" data-id="element-248">
                  <span className="atrium-headline-line" style={{
                  animationDelay: '0.42s'
                }} data-id="element-249">
                    of craftsmanship
                  </span>
                </span>
              </h1>
            </div>

            <p className="atrium-rise text-base md:text-lg text-atrium-ink/80 leading-relaxed mb-10 max-w-xl" style={{
            animationDelay: '0.5s'
          }} data-id="element-250">
              For over 50 years, the Marcolina family has designed and built
              award-winning luxury custom homes across Perth, with quality
              construction, attention to detail and personalised service.
            </p>

            <div className="atrium-rise flex flex-col sm:flex-row items-stretch sm:items-center gap-6 mb-6" style={{
            animationDelay: '0.6s'
          }} data-id="element-251">
              <Button variant="primary" as="a" href="#enquire" data-id="element-252">
                Book a Private Design Consultation
              </Button>
              <Button variant="secondary" as="a" href="tel:+61863108888" className="text-center leading-tight" aria-label="Call Atrium Homes on 0 8 6 3 1 0 8 8 8 8" data-id="element-253">
                <span className="flex flex-col items-center" data-id="element-254">
                  <span data-id="element-255">Call</span>
                  <span data-id="element-256">(08) 6310 8888</span>
                </span>
              </Button>
            </div>

            {/* Compact social proof cue near the primary CTA */}
            <div className="atrium-rise flex items-center gap-3 mb-10" style={{
            animationDelay: '0.7s'
          }} data-id="element-257">
              <span className="flex items-center gap-0.5" aria-hidden="true" data-id="element-258">
                {[0, 1, 2, 3, 4].map(i => <svg key={i} width="15" height="15" viewBox="0 0 24 24" className="text-atrium-bronze" fill="currentColor" data-id="element-259">
                    <path d="M12 2.5l2.9 5.88 6.5.94-4.7 4.58 1.11 6.47L12 17.83 6.19 20.9l1.1-6.47L2.6 9.85l6.5-.94L12 2.5z" data-id="element-260" />
                  </svg>)}
              </span>
              <span className="text-sm text-atrium-ink/70" data-id="element-261">
                <strong className="font-semibold text-atrium-ink" data-id="element-262">4.7</strong>{' '}
                Google rating from 21 reviews
              </span>
            </div>

            <div className="atrium-rise flex items-center gap-4" style={{
            animationDelay: '0.78s'
          }} data-id="element-263">
              <Button variant="tertiary" as="a" href="#latitude" data-id="element-264">
                Visit Latitude in Applecross
              </Button>
            </div>

            {/* Trust Line */}
            <div className="atrium-rise mt-16 pt-6 border-t border-atrium-stone/50" style={{
            animationDelay: '0.86s'
          }} data-id="element-265">
              <p className="text-xs font-medium text-atrium-ink/60 tracking-wide uppercase leading-loose" data-id="element-266">
                50+ years &nbsp;/&nbsp; Three generations &nbsp;/&nbsp;
                Family-owned &nbsp;/&nbsp; HIA & MBA recognised &nbsp;/&nbsp;
                Applecross display home
              </p>
            </div>
          </div>

          {/* Image Content (Right ~55%) */}
          <div className="w-full lg:w-[55%] relative" data-id="element-267">
            <div className="relative aspect-[4/5] md:aspect-[3/2] lg:aspect-[4/5] overflow-hidden" data-id="element-268">
              {/* swap = real Atrium completed-home exterior (hero feature) */}
              <img src="https://cdn.magicpatterns.com/uploads/nbyZzo7BiqBBBxN9tbEVQV/Dalkieht.webp" alt="Modern luxury custom home by Atrium Homes" width={1920} height={1080} fetchPriority="high" className="atrium-image object-cover w-full h-full" data-id="element-269" />
              {/* Subtle overlay for editorial feel */}
              <div className="absolute inset-0 bg-atrium-ink/5 mix-blend-multiply pointer-events-none" data-id="element-270" />

              {/* Animated corner brackets framing the image (the Atrium motif) */}
              <span className="atrium-bracket absolute top-4 left-4 w-8 h-8 border-t border-l border-atrium-ivory/80 pointer-events-none" style={{
              animationDelay: '0.9s'
            }} aria-hidden="true" data-id="element-271" />
              <span className="atrium-bracket absolute top-4 right-4 w-8 h-8 border-t border-r border-atrium-ivory/80 pointer-events-none" style={{
              animationDelay: '1s'
            }} aria-hidden="true" data-id="element-272" />
              <span className="atrium-bracket absolute bottom-4 left-4 w-8 h-8 border-b border-l border-atrium-ivory/80 pointer-events-none" style={{
              animationDelay: '1.05s'
            }} aria-hidden="true" data-id="element-273" />
              <span className="atrium-bracket absolute bottom-4 right-4 w-8 h-8 border-b border-r border-atrium-ivory/80 pointer-events-none" style={{
              animationDelay: '1.1s'
            }} aria-hidden="true" data-id="element-274" />
            </div>

            {/* Floating credential card overlapping the image (real award data) */}
            <div className="atrium-card relative -mt-10 ml-6 sm:-mt-12 lg:absolute lg:-bottom-8 lg:-left-8 lg:mt-0 lg:ml-0 w-[min(20rem,80%)]" data-id="element-275">
              <div className="relative bg-white p-6 shadow-diffuse" data-id="element-276">
                {/* Corner brackets motif */}
                <span className="absolute top-0 left-0 w-3.5 h-3.5 border-t border-l border-atrium-bronze opacity-70 pointer-events-none" data-id="element-277" />
                <span className="absolute top-0 right-0 w-3.5 h-3.5 border-t border-r border-atrium-bronze opacity-70 pointer-events-none" data-id="element-278" />
                <span className="absolute bottom-0 left-0 w-3.5 h-3.5 border-b border-l border-atrium-bronze opacity-70 pointer-events-none" data-id="element-279" />
                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 border-b border-r border-atrium-bronze opacity-70 pointer-events-none" data-id="element-280" />

                <div className="flex items-start gap-3" data-id="element-281">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-atrium-bronze mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-282">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" data-id="element-283" />
                  </svg>
                  <div data-id="element-284">
                    <span className="block text-[0.65rem] font-bold tracking-[0.15em] uppercase text-atrium-green mb-1" data-id="element-285">
                      Recognition
                    </span>
                    <p className="font-serif text-lg text-atrium-ink leading-snug" data-id="element-286">
                      2024 HIA Winner
                    </p>
                    <p className="text-sm text-atrium-ink/60" data-id="element-287">
                      Custom Built Home
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}